import { isSessionEnded, type AttachmentRef } from '@central/api';
import { isApiError } from '@central/ui';
import { api } from '../api';

/** What a new message asks Central to pull from its deployment. Empty services = all. */
export interface AttachChoice {
  logs: boolean;
  services: string[];
  health: boolean;
}

export const noAttach: AttachChoice = { logs: false, services: [], health: false };

/** Server limits: 10 attachments per message, 25 MiB each. */
export const MAX_FILES = 10;
export const MAX_FILE_BYTES = 26_214_400;

/**
 * Why these files cannot be sent, or undefined when they can. reserved is
 * how many of the message's attachments Central pulls from a deployment.
 */
export function fileProblem(files: File[], reserved = 0): string | undefined {
  if (files.length + reserved > MAX_FILES) return `Attach at most ${MAX_FILES - reserved} files.`;
  const big = files.find((f) => f.size > MAX_FILE_BYTES);
  if (big) return `${big.name} is larger than 25 MB.`;
  const empty = files.find((f) => f.size === 0);
  if (empty) return `${empty.name} is empty.`;
  return undefined;
}

/**
 * Uploads each file through the portal API: POST /attachments reserves it,
 * then the file is PUT to the returned same-origin URL with the returned
 * headers and the session's CSRF token. POST /attachments has its own
 * per-customer limit, so its 429 is told apart from sign-in's. An ended
 * session is passed on as it is; any other refused PUT is an UploadError.
 */
export async function uploadFiles(files: File[]): Promise<AttachmentRef[]> {
  const refs: AttachmentRef[] = [];
  for (const file of files) {
    const up = await api.attachments
      .presign({ name: file.name, contentType: file.type || undefined, bytes: file.size })
      .catch((e: unknown) => {
        if (isApiError(e, 'rate_limited'))
          throw new UploadError(
            file.name,
            'Too many uploads at once. Wait a minute and try again.',
          );
        throw e;
      });
    await api.attachments.upload(up, file).catch((e: unknown) => {
      if (isSessionEnded(e)) throw e;
      throw new UploadError(file.name);
    });
    refs.push({ key: up.key, bytes: file.size });
  }
  return refs;
}

export class UploadError extends Error {
  constructor(
    readonly fileName: string,
    message = `Couldn’t upload ${fileName}. Try again, or send the message without it.`,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}
